import { ref, computed } from 'vue'
import { useAccountsById } from './useAccountsById'

/**
 * Breadcrumb ancestors
 * There are two instances: activeItem and settingsItem
 * Includes hierarchy for all the plans (account, budget, campaign, ad group)
 * State persists at module level across component remounts
 */

const activeItemState = {
  loadingAncestors: ref(false),
  account: null, // Will be computed in the composable
  budget: shallowRef(null),
  campaign: shallowRef(null),
  adGroup: shallowRef(null)
}

const settingsItemState = {
  loadingAncestors: shallowRef(false),
  account: null, // Will be computed in the composable
  budget: shallowRef(null),
  campaign: shallowRef(null),
  adGroup: shallowRef(null),
  other: shallowRef(null) // ads, extensions, forms
}

const typesWithNoAncestors = ['account', 'budget', 'form']

export function useBreadcrumbAncestors (type = 'activeItem') {
  const { $store } = useNuxtApp()
  const { getAccountsById } = useAccountsById()
  const { accountsMinimal } = useAccountsMinimal()

  // Select the appropriate state based on type
  const state = type === 'activeItem' ? activeItemState : settingsItemState

  // Initialize computed properties if not already set
  // Either use current item or accounts minimal
  if (!state.account) {
    state.account = computed(() => {
      const item = type === 'activeItem' ? $store.getters.activeItem : $store.getters.settingsItem
      if (!item?.accountPlanId) return null
      if (item.planType === 'account') return item

      const accountById = getAccountsById().get(item.accountPlanId)

      return accountById || accountsMinimal().find((a) => a.accountPlanId === item.accountPlanId)
    })
  }

  const clearBreadcrumbs = () => {
    state.account.value = null
    state.budget.value = null
    state.campaign.value = null
    state.adGroup.value = null
    state.other.value = null
  }

  const fetchBreadcrumbAncestors = async () => {
    const item = type === 'activeItem' ? $store.getters.activeItem : $store.getters.settingsItem

    if (!item) {
      return
    }

    const planType = item.planType

    state.loadingAncestors.value = true

    if (planType === 'account') {
      // If they clicked on account, clear other breadcrumbs
      if (type === 'activeItem') {
        state.budget.value = null
        state.campaign.value = null
        state.adGroup.value = null
      }
    } else {
      try {
        const ancestors = await getAncestors(item, state)

        if (planType === 'budget') {
          state.budget.value = item
          if (type === 'activeItem') {
            state.campaign.value = null
            state.adGroup.value = null
          }
        } else {
          state.budget.value = ancestors.budgetPlan
        }

        if (planType === 'campaign') {
          state.campaign.value = item
          if (type === 'activeItem') {
            state.adGroup.value = null
          }
        } else {
          state.campaign.value = ancestors.campaignPlan
        }

        if (planType === 'adGroup') {
          state.adGroup.value = item
        } else {
          state.adGroup.value = ancestors.adGroupPlan
        }

        // ads, extensions, forms
        if (!['budget', 'campaign', 'adGroup'].includes(planType)) {
          state.other.value = item
        }
      } catch (e) {
        console.log(e)
      }
    }

    state.loadingAncestors.value = false
  }

  return {
    account: state.account,
    budget: state.budget,
    campaign: state.campaign,
    adGroup: state.adGroup,
    other: state.other, // ads, extensions, forms
    loadingAncestors: state.loadingAncestors,
    fetchBreadcrumbAncestors,
    clearBreadcrumbs
  }
}

const getAncestors = async (item, state) => {
  const planType = item.planType

  // account, budgets, forms
  if (typesWithNoAncestors.includes(planType)) {
    return {}
  }

  // campaigns
  if (planType === 'campaign') {
    return {
      budgetPlan: item.budgetPlan
    }
  }

  // ad groups
  if (planType === 'adGroup') {
    const campaign = await getCampaignAncestor(item, state.campaign)

    return {
      budgetPlan: campaign.budgetPlan,
      campaignPlan: campaign
    }
  }

  // ads, extensions
  const [campaign, adGroup] = await Promise.all([getCampaignAncestor(item, state.campaign), getAdGroupAncestor(item, state.adGroup)])

  return {
    budgetPlan: campaign?.budgetPlan || null,
    campaignPlan: campaign || null,
    adGroupPlan: adGroup || null
  }
}

const getCampaignAncestor = async (item, cachedItem) => {
  const { $res } = useNuxtApp()

  if (!item.campaignPlanId) return

  const campaignIsCached = cachedItem.value?.campaignPlanId === item.campaignPlanId

  if (campaignIsCached) {
    return cachedItem.value
  }

  const response = await $res.fetch.plan('campaign', { campaignPlanId: item.campaignPlanId })
  return response[0]
}

const getAdGroupAncestor = async (item, cachedItem) => {
  const { $res } = useNuxtApp()

  if (!item.adGroupPlanId) return

  const adGroupIsCached = cachedItem.value?.adGroupPlanId === item.adGroupPlanId

  if (adGroupIsCached) {
    return cachedItem.value
  }

  const response = await $res.fetch.plan('adGroup', { adGroupPlanId: item.adGroupPlanId })
  return response[0]
}
